import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import useComponentState from '../../utils/useComponentState';
import { clientGraphQL, getClientRedemptionsCSV } from '../../api/Client';
import Pagination from '../../components/pagination/Pagination.component';
import FilterOverlay from '../../components/FilterOverlay/FilterOverlay.component';
import './ViewClientRedemptions.scss';
import { formatDateTime } from '../../utils/dataFormatter';
import { DATE_TIME_TYPES } from '../../constants/dataTimeTypes';

const PAGE_SIZE = 10;
const CLIENT_VOUCHER_TYPE_MAP = {
    AIA: 'AIA_REWARDS',
    NDB: 'NDB_REWARDS',
};

const SEARCH_FIELD_OPTIONS = [
    { label: 'Redemption ID', value: 'id' },
    { label: 'Mobile No', value: 'voucher.mobileNo' },
    { label: 'Merchant', value: 'merchant.name' },
];

const BASE_FILTERS = [
    '{ key: "status", value: "complete" }',
];

const DEFAULT_SORT_BY = 'timestamp';
const DEFAULT_SORT_DIR = 'ASC';

const SORT_BY_OPTIONS = [
    { label: 'Date', value: 'timestamp' },
    { label: 'Amount', value: 'amount' },
    { label: 'Status', value: 'status' },
    { label: 'Redemption ID', value: 'id' },
];

const SORT_DIR_OPTIONS = [
    { label: 'Ascending', value: 'ASC' },
    { label: 'Descending', value: 'DESC' },
];

const ViewClientRedemptions = (props) => {
    const [state, setState] = useComponentState({
        list: [],
        pageNo: 0,
        totalPageCount: 0,
        loading: false,
        downloadingCSV: false,
        error: null,
        filters: {
            searchBy: 'id',
            search: '',
            fromDate: '',
            toDate: '',
            sortBy: DEFAULT_SORT_BY,
            sortDir: DEFAULT_SORT_DIR,
        },
        appliedFilters: {
            searchBy: 'id',
            search: '',
            fromDate: '',
            toDate: '',
            sortBy: DEFAULT_SORT_BY,
            sortDir: DEFAULT_SORT_DIR,
        },
        isFilterOverlayOpen: false,
    });
    const { auth, user } = props;

    const escapeGraphqlString = (value) =>
        String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

    const mapClientToVoucherType = (clientName) => {
        if (!clientName) {
            return null;
        }

        const normalized = String(clientName).trim().toUpperCase();
        return CLIENT_VOUCHER_TYPE_MAP[normalized] || normalized;
    };

    const getVoucherTypeFromUserDetails = () => {
        const authClientName = auth?.details?.clientName;
        const resolvedClientName = authClientName;

        return mapClientToVoucherType(resolvedClientName);
    };

    const buildFiltersQuery = (activeFilters, voucherType) => {
        const filtersFragments = [...BASE_FILTERS];

        if (voucherType) {
            filtersFragments.push(`{ key: "voucher.type", value: "${escapeGraphqlString(voucherType)}" }`);
        }

        const searchText = (activeFilters.search || '').trim();
        if (searchText) {
            filtersFragments.push(
                `{ key: "${escapeGraphqlString(activeFilters.searchBy || 'id')}", value: "${escapeGraphqlString(searchText)}" }`
            );
        }

        if (activeFilters.fromDate || activeFilters.toDate) {
            const dateRangeParts = [];
            if (activeFilters.fromDate) {
                dateRangeParts.push(`from: "${escapeGraphqlString(formatDateTime(activeFilters.fromDate, DATE_TIME_TYPES.ISO_DATE_TIME_OFFSET))}"`);
            }
            if (activeFilters.toDate) {
                dateRangeParts.push(`to: "${escapeGraphqlString(formatDateTime(activeFilters.toDate, DATE_TIME_TYPES.ISO_DATE_TIME_OFFSET))}"`);
            }
            filtersFragments.push(`{ key: "timestamp", ${dateRangeParts.join(', ')} }`);
        }

        return filtersFragments.join(',\n              ');
    };

    const fetchRedemptions = async (page = 0, activeFilters = state.appliedFilters) => {
        setState({ loading: true, error: null });
        const voucherType = getVoucherTypeFromUserDetails();
        const filtersQuery = buildFiltersQuery(activeFilters, voucherType);
        const sortBy = activeFilters.sortBy || DEFAULT_SORT_BY;
        const sortDir = activeFilters.sortDir || DEFAULT_SORT_DIR;
        const query = `
      {
        redemptionList(
          filters: {
            filters: [
              ${filtersQuery}
            ]
          }, 
          page: ${page}, 
          size: ${PAGE_SIZE},
          sortBy: "${escapeGraphqlString(sortBy)}",
          sortDir: "${escapeGraphqlString(sortDir)}"
        ) {
          pageNo
          totalPageCount
          list {
            id
            amount
            timestamp
            status
            merchant { id name }
            voucher { id type receiverName mobileNo }
          }
        }
      }
    `;
        try {
            const response = await clientGraphQL(query, auth.token);
            const data = response?.data?.redemptionList || {};
            setState({
                list: data.list || [],
                pageNo: data.pageNo || 0,
                totalPageCount: data.totalPageCount || 0,
                loading: false,
            });
        } catch (err) {
            setState({ loading: false, error: err?.message || 'Failed to load data' });
        }
    };

    useEffect(() => {
        if (!auth?.token) {
            return;
        }

        fetchRedemptions(0, state.appliedFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.token, auth?.details?.clientName, user?.currentUser?.clientName]);

    const handlePageChange = (selectedPage) => {
        fetchRedemptions(selectedPage, state.appliedFilters);
    };

    const handleFilterChange = (key, value) => {
        setState({
            filters: {
                ...state.filters,
                [key]: value,
            },
        });
    };

    const applyFilters = () => {
        const nextAppliedFilters = {
            ...state.filters,
        };

        setState({
            appliedFilters: nextAppliedFilters,
            isFilterOverlayOpen: false,
        });

        fetchRedemptions(0, nextAppliedFilters);
    };

    const clearFilters = () => {
        const resetFilters = {
            searchBy: 'id',
            search: '',
            fromDate: '',
            toDate: '',
            sortBy: DEFAULT_SORT_BY,
            sortDir: DEFAULT_SORT_DIR,
        };

        setState({
            filters: resetFilters,
            appliedFilters: resetFilters,
            isFilterOverlayOpen: false,
        });

        fetchRedemptions(0, resetFilters);
    };

    const toggleFilterOverlay = (isOpen) => {
        setState({ isFilterOverlayOpen: isOpen });
    };

    const downloadCSV = async () => {
        setState({ downloadingCSV: true, error: null });
        const voucherType = getVoucherTypeFromUserDetails();

        const filters = [...BASE_FILTERS.map(f => eval(`(${f})`))];

        if (voucherType) {
            filters.push({ key: "voucher.type", value: voucherType });
        }

        const searchText = (state.appliedFilters.search || '').trim();
        if (searchText) {
            filters.push({
                key: state.appliedFilters.searchBy || 'id',
                value: searchText
            });
        }

        if (state.appliedFilters.fromDate || state.appliedFilters.toDate) {
            const dateRange = {};
            if (state.appliedFilters.fromDate) {
                dateRange.from = formatDateTime(state.appliedFilters.fromDate, DATE_TIME_TYPES.ISO_DATE_TIME_OFFSET);
            }
            if (state.appliedFilters.toDate) {
                dateRange.to = formatDateTime(state.appliedFilters.toDate, DATE_TIME_TYPES.ISO_DATE_TIME_OFFSET);
            }
            filters.push({ key: "timestamp", ...dateRange });
        }

        try {
            const sortBy = state.appliedFilters.sortBy || DEFAULT_SORT_BY;
            const sortDir = state.appliedFilters.sortDir || DEFAULT_SORT_DIR;
            const response = await getClientRedemptionsCSV({ filters }, auth.token, sortBy, sortDir);
            const blob = new Blob([response], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'redemptions.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            setState({ downloadingCSV: false, error: err?.message || 'Failed to download CSV' });
            return;
        } finally {
            setState({ downloadingCSV: false });
        }
    };

    const { list, pageNo, totalPageCount, loading, downloadingCSV, error } = state;
    const hasAppliedFilters = Boolean(
        state.appliedFilters.search || state.appliedFilters.fromDate ||
        state.appliedFilters.toDate ||
        state.appliedFilters.sortBy !== DEFAULT_SORT_BY ||
        state.appliedFilters.sortDir !== DEFAULT_SORT_DIR
    );

    return (
        <div className="redemptions_table_root">
            <div className="page_title_row">
                <h2>Redemptions</h2>
                <div className="page_title_line" />
            </div>
            <div className="filters_toolbar">
                <button
                    type="button"
                    className="open_filter_btn"
                    onClick={() => toggleFilterOverlay(true)}
                    aria-label="Open filters"
                    title="Open filters"
                >
                    <FontAwesomeIcon icon={faFilter} className="open_filter_icon" />
                </button>
                <button
                    type="button"
                    className="download_csv_btn"
                    onClick={downloadCSV}
                    disabled={downloadingCSV}
                    aria-label="Download CSV"
                    title="Download CSV"
                >
                    {downloadingCSV ? 'Downloading...' : 'Download CSV'}
                </button>
                {hasAppliedFilters && (
                    <button
                        type="button"
                        className="quick_reset_btn"
                        onClick={clearFilters}
                        aria-label="Reset filters"
                        title="Reset filters"
                    >
                        Reset
                    </button>
                )}
            </div>
            <FilterOverlay
                isOpen={state.isFilterOverlayOpen}
                title="Redemption Filters"
                filters={state.filters}
                dropdownDetails={[
                    {
                        key: 'searchBy',
                        label: 'Search By',
                        options: SEARCH_FIELD_OPTIONS,
                        section: 'filter',
                    },
                    {
                        key: 'sortBy',
                        label: 'Sort By',
                        options: SORT_BY_OPTIONS,
                        section: 'sort',
                    },
                    {
                        key: 'sortDir',
                        label: 'Sort Direction',
                        options: SORT_DIR_OPTIONS,
                        section: 'sort',
                    },
                ]}
                onChange={handleFilterChange}
                onApply={applyFilters}
                onReset={clearFilters}
                onClose={() => toggleFilterOverlay(false)}
            />
            {loading ? (
                <div className="state_message">Loading...</div>
            ) : error ? (
                <div className="state_message state_message_error">{error}</div>
            ) : (
                <>
                    <div className="redemptions_table_container">
                        <div className="redemptions_header">
                            <span>Date</span>
                            <span>Redemption ID</span>
                            <span>Mobile No</span>
                            <span>User</span>
                            <span>Amount (LKR)</span>
                            <span>Status</span>
                            <span>Merchant</span>
                        </div>
                        <div className="redemptions_list">
                            {list.length === 0 ? (
                                <div className="state_message">No redemptions match the current filters.</div>
                            ) : (
                                list.map((item) => (
                                    <div key={item.id} className="redemption_row">
                                        <span>
                                            {item.timestamp
                                                ? new Date(item.timestamp).toLocaleString('en-IN', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })
                                                : ''}
                                        </span>
                                        <span>{item.id}</span>
                                        <span>{item.voucher?.mobileNo}</span>
                                        <span>{item.voucher?.receiverName}</span>
                                        <span>LKR {item.amount?.toFixed(2)}</span>
                                        <span className={`status status_${item.status?.toLowerCase()}`}>{item.status}</span>
                                        <span>{item.merchant?.name}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    <div className="pagination_toolbar">
                        <Pagination
                            pageCount={totalPageCount}
                            currentPage={pageNo}
                            onChange={handlePageChange}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth.auth,
    user: state.user,
});

export default connect(mapStateToProps)(ViewClientRedemptions);